/**
 * Microdata configuration for JSON-LD structured data
 * Used for SEO and rich results in search engines
 */

export const microdataConfig = {
  site: {
    name: 'Tytum',
    url: 'https://tytum.com/',
    logo: 'https://tytum.com/opengraph-image.png',
    defaultImage: 'https://tytum.com/og/og-default.jpg',
    email: 'contact@tytum.com',
    phone: '+16193494397',
    languages: ['English', 'Spanish'],
    social: {
      instagram: 'https://www.instagram.com/tytum_hvac/',
      facebook:
        'https://www.facebook.com/people/Tytum-Heating-and-AC/61559727104902/',
    },
  },
  homepage: {
    title:
      'Tytum — HVAC, Electrical, Plumbing, and Other Services in San Diego',
    description:
      'From HVAC in San Diego to plumbing, electrical, construction, and remodeling services, we offer solutions tailored to your home or business needs.',
    url: 'https://tytum.com/',
    datePublished: '2024-01-01T00:00:00-08:00',
    dateModified: new Date().toISOString(),
  },
  local: {
    name: 'Tytum',
    address: {
      street: '16950 Via De Santa Fe, Suite 5060-136',
      city: 'Rancho Santa Fe',
      region: 'CA',
      postal: '92091',
      country: 'US',
    },
    geo: {
      lat: 33.0186248,
      lng: -117.2026129,
    },
    hours: {
      weekdays: {
        opens: '07:00',
        closes: '22:00',
      },
    },
    priceRange: '$$',
    mapUrl: 'https://maps.app.goo.gl/hv9WaAWryE6a6Bwb6',
    areaServed: ['San Diego County', 'Greater San Diego Area'],
  },
  services: {
    url: 'https://tytum.com/services',
    hvac: {
      install:
        'https://tytum.com/services/heating-and-air/heating-installation',
      repair: 'https://tytum.com/services/heating-and-air/heating',
      maintenance:
        'https://tytum.com/services/heating-and-air/heating-maintenance',
      acInstall: 'https://tytum.com/services/heating-and-air/ac-installation',
      acRepair: 'https://tytum.com/services/heating-and-air/air-conditioning',
      ductless:
        'https://tytum.com/services/heating-and-air/cooling-and-heating',
    },
    electrical: {
      install: 'https://tytum.com/services/electrical',
      panel: 'https://tytum.com/services/electrical#panel-upgrades',
      repair: 'https://tytum.com/services/electrical#electrical-repair',
    },
    plumbing: {
      install: 'https://tytum.com/services/plumbing',
      repair: 'https://tytum.com/services/plumbing#plumbing-repair',
      waterHeaters: 'https://tytum.com/services/plumbing#water-heaters',
    },
    remodeling: {
      kitchen: 'https://tytum.com/services/remodeling#kitchen',
      bathroom: 'https://tytum.com/services/remodeling#bathroom',
    },
    construction: {
      residential: 'https://tytum.com/services/construction#residential',
      commercial: 'https://tytum.com/services/construction#commercial',
    },
  },
  about: {
    url: 'https://tytum.com/company/about',
    title: 'About Us — Reliable HVAC, Electrical & Plumbing Services',
    description:
      'Learn more about Tytum and our commitment to providing quality HVAC, electrical, plumbing, and construction services in San Diego County.',
    image: 'https://tytum.com/og/og-company.jpg',
    datePublished: '2024-01-01T00:00:00-08:00',
    dateModified: new Date().toISOString(),
    breadcrumbParentName: 'Company',
    breadcrumbParentUrl: 'https://tytum.com/company',
  },
  organization: {
    slogan: 'Your Trusted Partner for Home Services in San Diego',
    foundingDate: '2009',
  },
  contact: {
    url: 'https://tytum.com/company/contact',
    title: 'Contact Us — Get in Touch with Tytum',
    description:
      'Contact Tytum for reliable HVAC, electrical, plumbing, and construction services in San Diego County. Reach us by phone, email, or visit our office.',
    languages: ['English', 'Spanish'],
    areaServed: 'US-CA',
  },
  faq: [
    {
      question: 'What types of services does Tytum offer?',
      answer:
        'We offer a wide range of services, including: Heating and Air Conditioning Services, Electrical Services, Plumbing Services, Remodeling Services, New Construction Services, and Real Estate Services.',
    },
    {
      question: 'Does Tytum offer emergency services?',
      answer:
        'Yes, Tytum offers 24/7 emergency services for both HVAC and plumbing issues, ensuring fast and reliable assistance whenever you need it.',
    },
    {
      question: 'What areas does Tytum serve?',
      answer:
        'We provide services throughout the Greater San Diego Area, including East County, Central San Diego, North County, and South Bay.',
    },
    {
      question: 'What heating and air conditioning services does Tytum offer?',
      answer:
        'We offer a range of heating and cooling services, including: Furnace Repair and Maintenance, Heating System Installation and Replacement, Heat Pump Services, Ductless Heating Systems, Air Conditioning Repair and Maintenance, Air Conditioning Installation and Replacement, Central Air Conditioning Systems, Ductless Air Conditioning Systems, and Energy-Efficient AC Upgrades.',
    },
    {
      question: 'Does Tytum provide maintenance plans?',
      answer:
        'Yes, Tytum offers seasonal maintenance plans for HVAC systems, electrical systems, and plumbing. These plans are designed to keep your home systems running efficiently year-round.',
    },
    {
      question: "How can Tytum help improve my home's energy efficiency?",
      answer:
        'Tytum offers energy efficiency consulting services such as: Home Energy Audits, HVAC System Efficiency Upgrades, and Recommendations for Energy Savings.',
    },
    {
      question:
        'Why should I choose Tytum for my heating and air conditioning needs?',
      answer:
        'Tytum is committed to providing top-quality services with a focus on customer satisfaction. We offer certified technicians, transparent pricing, and personalized service. As a locally owned company, we are deeply committed to serving the San Diego community.',
    },
    {
      question: 'Can Tytum help with thermostat installation and upgrades?',
      answer:
        'Absolutely! We install smart thermostats, provide thermostat repair and replacement services, and offer energy-efficient thermostat solutions.',
    },
  ],
} as const;
